import { useCallback } from 'react';
import { DiscountCodeState, GeoDiscount } from 'shared/types/cart/Discount';
import { AlertCode } from 'shared/types/checkout';
import {
  getBillingCountry,
  getCookieCountry,
  getCurrentGeoDiscount,
  getIsAutoApplied,
  getIsValidating,
  setAlert,
  setBillingCountry,
  setCurrentGeoDiscount,
  setIsAutoApplied,
  useAppDispatch,
  useAppSelector,
} from 'store';
import { hasBundleId } from 'utils/bundle-id';
import { getGeoDiscountFailureAlert } from 'utils/discount/getGeoDiscountFailureAlert';
import { useCart } from '../useCart';

type GeoDiscountReturnable = {
  billingCountry: string;
  cookieCountry: string;
  currentGeoDiscount: {
    code: string;
    discountId: string;
  };
  isAutoApplied: boolean;
  isValidating: boolean;
  resetAutoAppliedFlag: () => void;
  setBillingCountry: (country: string) => void;
  validateAndUpdateDiscount: (coupon: GeoDiscount) => Promise<void>;
  autoApplyDiscount: (coupon: GeoDiscount) => Promise<void>;
};

const useGeoDiscount = (): GeoDiscountReturnable => {
  const dispatch = useAppDispatch();

  const { data, redeemDiscountCode, removeDiscountCode } = useCart();

  const cookieCountry = useAppSelector(getCookieCountry);
  const billingCountry = useAppSelector(getBillingCountry);
  const isAutoApplied = useAppSelector(getIsAutoApplied);
  const currentGeoDiscount = useAppSelector(getCurrentGeoDiscount);

  const autoApplyDiscount = useCallback(
    async (discount: GeoDiscount) => {
      if (!data?.cartId) {
        return;
      }

      const { cartId } = data;

      // If cart is a program (identified by bundleId on line items from getCart,
      // not getProgramInfo), remove all existing discount codes first so only the
      // geo discount is applied
      const lineItems = data?.lineItems ?? [];
      const isProgram = hasBundleId(lineItems);
      if (isProgram && data?.discountCodes && data.discountCodes.length > 0) {
        // Remove all existing discount codes before applying geo discount
        await Promise.all(
          data.discountCodes
            .filter(
              (
                existingDiscount,
              ): existingDiscount is typeof existingDiscount & {
                discountId: string;
                code: string;
              } => !!existingDiscount.discountId && !!existingDiscount.code,
            )
            .map((existingDiscount) =>
              removeDiscountCode(cartId)(
                {
                  code: existingDiscount.code,
                  discountId: existingDiscount.discountId,
                },
                {},
                true, // Silent removal
              ),
            ),
        );
      }

      const response = await redeemDiscountCode(cartId)(
        discount.discountCode,
        false,
        false,
      );

      if (response.isError) {
        dispatch(
          setAlert(
            getGeoDiscountFailureAlert(
              discount,
              AlertCode.GEO_DISCOUNT_APPLY_FAILED,
              response.errorCode ?? null,
            ),
          ),
        );
        dispatch(
          setCurrentGeoDiscount({
            code: '',
            discountId: '',
            percentage: '',
          }),
        );
        return;
      }

      const validDiscount = response?.discounts?.find(
        (disc) => disc.state === DiscountCodeState.MATCHES_CART,
      );

      dispatch(
        setAlert({
          code: AlertCode.GEO_DISCOUNT_APPLIED,
          value: `${discount?.discountCode}/${discount?.discountPercentage}%`,
        }),
      );

      dispatch(setIsAutoApplied(true));
      dispatch(
        setCurrentGeoDiscount({
          code: discount.discountCode,
          discountId: validDiscount?.discountId ?? '',
          percentage: String(discount?.discountPercentage ?? ''),
        }),
      );
    },
    [
      redeemDiscountCode,
      removeDiscountCode,
      data?.cartId,
      data?.lineItems,
      data?.discountCodes,
      dispatch,
    ],
  );

  const validateAndUpdateDiscount = useCallback(
    async (discount: GeoDiscount) => {
      if (!data?.cartId || currentGeoDiscount?.code === discount.discountCode) {
        return;
      }

      if (currentGeoDiscount?.code) {
        const amount = currentGeoDiscount?.percentage
          ? `${currentGeoDiscount?.percentage}%`
          : '';

        await removeDiscountCode(data.cartId)(
          {
            code: currentGeoDiscount.code,
            discountId: currentGeoDiscount.discountId,
          },
          {
            type: AlertCode.GEO_COUNTRY_MISMATCH,
            value: amount,
          },
          !!discount.discountCode,
        );
      }

      if (isAutoApplied && !discount.discountCode) {
        dispatch(
          setCurrentGeoDiscount({
            code: '',
            discountId: '',
            percentage: '',
          }),
        );

        return;
      }

      await autoApplyDiscount(discount);
    },
    [
      autoApplyDiscount,
      currentGeoDiscount.code,
      currentGeoDiscount.discountId,
      data?.cartId,
      isAutoApplied,
      removeDiscountCode,
      dispatch,
    ],
  );

  return {
    cookieCountry,
    billingCountry,
    currentGeoDiscount,
    isAutoApplied: useAppSelector(getIsAutoApplied),
    isValidating: useAppSelector(getIsValidating),
    resetAutoAppliedFlag: () => dispatch(setIsAutoApplied(false)),
    setBillingCountry: (country: string) =>
      dispatch(setBillingCountry(country)),
    autoApplyDiscount,
    validateAndUpdateDiscount,
  };
};

export { useGeoDiscount };
